import { ROLES } from "@infinitaslearning/module-he-common";
import { PlayerWrapper } from "@infinitaslearning/module-player2";
import { getAnalyticsTracker } from "analytics";
import { ASSETS_TYPES, PLAYER_TRANSLATIONS } from "constants/index";
import { useTest } from "hooks/useTest";
import { toJS } from "mobx";
import { observer } from "mobx-react";
import type { PathParams } from "models/path/Path";
import { getAccountRole } from "persistence/access";
import type React from "react";
import { useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import type { PlayerProps } from "./Player.model";
import { StyleAudioFile, StyleDiv, StyleVideo } from "./Player.styled";

export const Player: React.FC<PlayerProps> = observer(
  ({ initialData, dataCy, nextQuestion, questionAnswered }) => {
    const history = useHistory();
    const { chapterId, additionalsId }: PathParams = useParams();
    const { postTestResult } = useTest();
    const data = initialData.topics
      ? toJS(initialData)
      : { topics: [[toJS(initialData)]], interactions: {} };
    const [submitted, setSubmitted] = useState(false);
    const analyticsTracker = getAnalyticsTracker();
    if (initialData._type === ASSETS_TYPES.video) {
      const video = {
        id: initialData._id,
        portableTextContent: toJS(initialData),
      };
      return (
        <StyleVideo data-cy={dataCy}>
          <PlayerWrapper
            key={video.id}
            analyticsTracker={analyticsTracker}
            config={{
              superSubmitButton: null,
              assetListRenderer: false,
            }}
            id={video.id}
            initialData={{ topics: [[video]], interactions: {} }}
          />
        </StyleVideo>
      );
    }
    if (initialData._type === ASSETS_TYPES.audio) {
      const audio = {
        id: initialData._id,
        portableTextContent: toJS(initialData),
      };
      return (
        <StyleAudioFile>
          <PlayerWrapper
            key={audio.id}
            analyticsTracker={analyticsTracker}
            config={{
              superSubmitButton: null,
              assetListRenderer: false,
            }}
            id={audio.id}
            initialData={{ topics: [[audio]], interactions: {} }}
          />
        </StyleAudioFile>
      );
    }
    const role = getAccountRole();
    const finishExam = (dataFromPlayer: any) => {
      const parsedAnsweredQuestions = Object.keys(dataFromPlayer)
        .map((questionId) => ({
          id: questionId,
          isCorrect: +dataFromPlayer[questionId].score === 1,
        }))
        .filter((question) => +question.id !== 0);
      role === ROLES.STUDENT && postTestResult(chapterId, parsedAnsweredQuestions);
      if (role === ROLES.STUDENT) {
        analyticsTracker?.elementClicked({
          elementId: "finish-chapter-exam-login",
          module: "chapter-exam",
          type: "button",
          itemId: "finish-chapter-exam-button",
        });
      }
      history.push(`/${additionalsId}`);
    };

    const passNextQuestion = (questionsAnswered: number) => {
      setSubmitted(false);
      if (nextQuestion) {
        nextQuestion(questionsAnswered);
      }
    };

    const listeners = {
      onSubmitAnswer: () => {
        setSubmitted(true);
        if (questionAnswered) {
          questionAnswered();
        }
      },
      onNext: (res: number) => passNextQuestion(res),
      onRetryAnswer: () => setSubmitted(false),
      onFinish: (res: any) => finishExam(res),
    };
    return (
      <StyleDiv data-cy={dataCy} submitted={submitted}>
        <PlayerWrapper
          key={initialData.id}
          analyticsTracker={analyticsTracker}
          config={{
            shuffleAnswer: {
              singleSelect: "yes",
              lcSingleSelect2: "yes",
              multipleSelect: "yes",
              lcMultipleSelect2: "yes",
              inlineChoice: "yes",
            },
            translations: PLAYER_TRANSLATIONS,
            retryButton: null,
            disableMissingAnswerWarning: true,
            showRightAnswer: { all: true },
          }}
          id={initialData.id || "player-wrapper"}
          initialData={data}
          listeners={listeners}
        />
      </StyleDiv>
    );
  },
);
